import { useEffect, useMemo, useState, Fragment, useCallback, type ReactNode } from 'react'
import { Table, Tabs, Button, Progress, Tag, message, Spin, Empty, Collapse, Pagination, Select, Modal, DatePicker } from 'antd'
import { FileOutlined, UserOutlined, CalendarOutlined, StarFilled, CloseOutlined } from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import dayjs, { type Dayjs } from 'dayjs'
import type { BacktestDeatil, NavPoint, BacktestTradesDetail, BacktestReasonDetail, AISignal, AISignalSummary, HoldingsSnapshot } from '@/types'
import { getBacktestDetail, getBacktestNavDetail, getBacktestBenchmarkNav, getBacktestStockPoolNav, getBacktestTradesDetail, getBacktestNavDrawdownDetail, archiveBacktest, deleteBacktest, getBacktestReasonDetail, getAISignals, getAISignalDetail, getBacktestHoldings } from '@/api'
import { filterFormatThousandNumber, filterFormatWanYiNumber } from '@/utils/filters'
import NavChart from '@/components/NavChart'
import './index.scss'
import type { ColumnsType } from 'antd/es/table'
import { MdViewer } from '@/components/MarkdownFile'
import { benchmarkOptions, DEFAULT_BENCHMARK, normalizeBenchmarkLabel } from '@/pages/static'

type DetailTabKey = 'returns' | 'stats' | 'trades' | 'holdings' | 'reason' | 'config' | 'ai_signals'
type MetricValueKey = keyof BacktestDeatil | `results.${keyof BacktestDeatil['results']}` | `config.${keyof BacktestDeatil['config']}`

type MetricCard = {
    label: string
    value: MetricValueKey
    subLabel?: string
    subValue?: string
    subUnit?: string
    tone?: boolean
    unit?: string
}

const tabs: Array<{ key: DetailTabKey; label: string }> = [
    { key: 'returns', label: '收益曲线' },
    { key: 'stats', label: '统计详情' },
    { key: 'trades', label: '交易记录' },
    { key: 'holdings', label: '持仓查询' },
    { key: 'reason', label: '持仓归因' },
    { key: 'config', label: '配置快照' },
    { key: 'ai_signals', label: 'AI决策信号' },
]

type aiSignalsKeys = 'fundamental' | 'kline' | 'report'

const aiSignalsTabs: Array<{ key: aiSignalsKeys, label: string }> = [
    { key: 'fundamental', label: '调仓与基本面' },
    { key: 'kline', label: 'K线信号' },
    { key: 'report', label: '研报信号' },
]

const metricCards: MetricCard[] = [
    { label: '总收益率', value: 'final_cumulative_return_pct', unit: '%', subLabel: '年化', subValue: 'annual_return_pct', subUnit: '%', tone: true },
    { label: '夏普比率', value: 'sharpe', unit: '', },
    { label: '最大回撤', value: 'max_drawdown_pct', unit: '%', subLabel: '持续', subValue: 'max_drawdown_days', subUnit: '天', tone: true },
    { label: '胜率', value: 'win_rate', unit: '%', subLabel: '盈亏比', subValue: 'profit_factor' },
    { label: '总交易次数', value: 'order_fills', unit: '次' },
    { label: '超额收益', value: 'results.excessReturn', unit: '%', subLabel: 'Beta', subValue: 'results.beta', subUnit: '', tone: true },
]

type StatsSectionItem = {
    label: string
    value: MetricValueKey
    tone?: boolean
    unit?: string
    orderColor?: 'green' | 'red'
    prefix?: string
}

type StatsSection = {
    title: string
    items: StatsSectionItem[]
}

const statsSections: StatsSection[] = [
    {
        title: '收益指标',
        items: [
            { label: '总收益率', value: 'final_cumulative_return_pct', tone: true, unit: '%' },
            { label: '年化收益率', value: 'annual_return_pct', tone: true, unit: '%' },
            { label: '基准收益率', value: 'results.benchmarkReturn', unit: '%' },
            { label: '超额收益', value: 'results.excessReturn', unit: '%' },
            { label: '年化 Alpha', value: 'results.alpha', unit: '%' },
            { label: '期末净值', value: 'results.endValue', prefix: '¥' },
            { label: '初始资金', value: 'initial_cash', prefix: '¥' },
        ],
    },
    {
        title: '风险指标',
        items: [
            { label: '最大回撤', value: 'max_drawdown_pct', tone: true, unit: '%' },
            { label: '最大回撤持续', value: 'max_drawdown_days', unit: '天' },
            { label: '夏普比率', value: 'sharpe' },
            // { label: '索提诺比率', value: 'sharpe', tone: true, },
            { label: '卡玛比率', value: 'results.calmar', tone: true },
            { label: 'Beta', value: 'results.beta' },
        ],
    },
    {
        title: '交易统计',
        items: [
            { label: '总交易次数', value: 'order_fills' },
            { label: '盈利次数', value: 'results.profitTrades', orderColor: 'red' },
            { label: '亏损次数', value: 'results.lossTrades', orderColor: 'green' },
            { label: '胜率', value: 'win_rate', tone: true, unit: '%' },
            { label: '平均盈利', value: 'results.avgProfit', orderColor: 'red', prefix: '￥' },
            { label: '平均亏损', value: 'results.avgLoss', orderColor: 'green', prefix: '￥' },
        ],
    },
    {
        title: '组合配置',
        items: [
            { label: 'K线级别', value: 'config.freq' },
            { label: '最大持仓数', value: 'config.maxPositions' },
            { label: '手续费率', value: 'config.commission', unit: '‰' },
            { label: '仓位分配', value: 'config.positionSizing' },
            { label: '止损设置', value: 'config.stopLoss', unit: '%' },
            { label: '监控信号', value: 'config.useMonitorSignals', orderColor: 'green' },
        ],
    },
]

const { RangePicker } = DatePicker
const SIGNALS_PAGE_SIZE = 10

function tradeDateText(value: string): string {
    return value.slice(0, 10)
}

function investedPositionPct(item: NavPoint): number | null {
    const total = Number(item.total_assets)
    const cash = Number(item.cash)
    if (!Number.isFinite(total) || !Number.isFinite(cash) || total <= 0) {
        return null
    }
    const pct = ((total - cash) / total) * 100
    if (!Number.isFinite(pct)) {
        return null
    }
    return Math.min(100, Math.max(0, pct))
}

function inSelectedRange(tradeDate: string, range: [string, string] | null): boolean {
    if (!range) {
        return true
    }
    const date = tradeDateText(tradeDate)
    return date >= range[0] && date <= range[1]
}

function positiveNav(value: unknown): number | null {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null
}

/** 各曲线除以自己在窗口内的首个净值，起点都变成 1，之后的高低就是这段区间的涨跌。 */
function rebaseToWindowStart(points: NavPoint[]): NavPoint[] {
    const keys = ['nav_value', 'benchmark_nav', 'pool_nav'] as const
    const bases = {} as Record<(typeof keys)[number], number | null>
    keys.forEach((key) => {
        let earliest = ''
        let base: number | null = null
        points.forEach((item) => {
            const value = positiveNav(item[key])
            if (value == null) {
                return
            }
            const date = tradeDateText(item.trade_date)
            if (base == null || date < earliest) {
                earliest = date
                base = value
            }
        })
        bases[key] = base
    })

    return points.map((item) => {
        const next = { ...item }
        keys.forEach((key) => {
            const base = bases[key]
            const value = positiveNav(item[key])
            if (base && value != null) {
                next[key] = value / base
            }
        })
        return next
    })
}

const SIGNAL_TYPES: Record<aiSignalsKeys, string> = {
    fundamental: 'REBALANCE,REPORT_PUBLISHED',
    kline: 'STOP_LOSS,TAKE_PROFIT,PE_CEILING',
    report: 'RESEARCH_UPDATE',
}

type ThinkingStep = {
    step: number
    tool: string
    args?: Record<string, unknown>
    result_preview?: string
}

const TOOL_LABELS: Record<string, string> = {
    get_current_portfolio:    '获取当前持仓',
    get_universe:             '获取可投资标的',
    screen_stocks:            '筛选候选股票',
    query_fundamentals:       '查询基本面数据',
    analyze_candidates:       '深度研究候选股',
    submit_portfolio_decision:'提交目标组合提案',
}

type _FilterRow = { field: string; operator: string; value: number | string }
type _OrderRow2 = { code: string; target_weight: number; reason?: string }

type DecisionAdjustment = {
    code: string
    proposed: number
    final: number
}

const DECISION_OUTCOME_LABELS: Record<string, { label: string; color: string }> = {
    ACCEPTED: { label: '决策通过', color: 'success' },
    NO_ACTION: { label: '无需操作', color: 'default' },
    FALLBACK_HOLD: { label: '降级维持', color: 'warning' },
    DATA_INSUFFICIENT: { label: '数据不足', color: 'warning' },
    RISK_REJECTED: { label: '风控拒绝', color: 'error' },
    RISK_REVIEW_FAILED: { label: '风控复核失败', color: 'error' },
    AGENT_ERROR: { label: '决策失败', color: 'error' },
}

function signalEventLabel(signal: Pick<AISignalSummary, 'event_type' | 'event_source' | 'signal_type'> & {
    trigger_detail?: Record<string, unknown>
}) {
    const eventType = signal.event_type ?? (signal.trigger_detail?.event_type as string | undefined)
    const eventSource = signal.event_source ?? (signal.trigger_detail?.event_source as string | undefined)
    if (eventSource === 'initial_build') return '首次建仓'
    if (eventType && eventType !== 'FULL') return eventType
    const fallback: Record<string, string> = {
        REBALANCE: '调仓决策',
        REPORT_PUBLISHED: '财报发布',
        STOP_LOSS: '止损信号',
        TAKE_PROFIT: '止盈信号',
        PE_CEILING: '估值上限信号',
        RESEARCH_UPDATE: '研报更新',
    }
    return fallback[signal.signal_type] ?? signal.signal_type
}

function targetWeightOf(orders: _OrderRow2[]) {
    return orders.reduce((total, order) => total + Number(order.target_weight || 0), 0)
}

function latestProposalOrders(steps: ThinkingStep[]): _OrderRow2[] {
    const submissions = steps.filter(step => step.tool === 'submit_portfolio_decision')
    const raw = submissions[submissions.length - 1]?.args?.orders
    return Array.isArray(raw) ? raw as _OrderRow2[] : []
}

function decisionAdjustments(proposal: _OrderRow2[], finalOrders: _OrderRow2[]): DecisionAdjustment[] {
    const proposed = new Map(proposal.map(order => [order.code, Number(order.target_weight || 0)]))
    const final = new Map(finalOrders.map(order => [order.code, Number(order.target_weight || 0)]))
    return [...new Set([...proposed.keys(), ...final.keys()])]
        .map(code => ({ code, proposed: proposed.get(code) ?? 0, final: final.get(code) ?? 0 }))
        .filter(item => Math.abs(item.proposed - item.final) > 1e-6)
}

type StockReport = {
    code: string
    recommendation: 'buy' | 'hold' | 'avoid'
    confidence: number
    thesis: string
    evidence?: string[]
    risks?: string[]
    invalidation_conditions?: string[]
    expected_holding_days?: number | null
}

type PerFilterCount = {
    field?: string
    operator?: string
    value?: unknown
    matched?: number
    missing?: number
    invalid?: boolean
}

type ScreeningDetail = {
    screening_mode?: string
    filters_applied?: Array<{ field?: string; operator?: string; value?: unknown }>
    matched_count?: number
    returned_count?: number
    post_gate_count?: number
    eligible_candidate_count?: number
    eligible_candidate_codes?: string[]
    retained_holding_count?: number
    retained_holding_codes?: string[]
    excluded_after_match_count?: number
    total_universe?: number
    per_filter_counts?: PerFilterCount[]
    invalid_filters?: Array<{ field?: string; reason?: string }>
}

type DeterministicScore = {
    strategy_type?: string
    total_score?: number | null
    complete?: boolean
    subscores?: Record<string, number | null>
    action_bucket?: string | null
    hard_excluded?: boolean
    hard_exclusion_reasons?: string[]
    hard_filter_missing_fields?: string[]
    report_stale?: boolean
    report_age_days?: number
    warnings?: string[]
}

const SUBSCORE_LABELS: Record<string, string> = {
    revenue_growth: '收入增长',
    revenue_acceleration: '收入加速',
    gross_profit_leverage: '毛利杠杆',
    operating_leverage: '经营杠杆',
    ocf_sync: 'OCF 同步性',
    igpe: '增量毛利转化率',
    da_pressure: '折旧摊销压力',
    asset_turnover: '固定资产周转改善',
    ocf_to_capex: 'OCF 转化杠杆',
    adjusted_margin: '降噪净利率改善',
    sbc: 'SBC 强度',
    peg: 'PEG 匹配度',
    valuation_percentile: '历史估值分位',
    net_debt_health: '净债务状态',
    dilution: '股本稀释',
    gross_margin: '毛利率',
    growth_stability: '增长稳定性',
    profit_cashflow: '盈利/现金流',
    shareholder_return: '股东回报',
    cashflow_coverage: '现金流覆盖',
    dividend_stability: '分红稳定',
    balance_sheet: '资产负债',
    fundamental_stability: '基本面',
    valuation: '估值',
}

const ACTION_BUCKET_LABELS: Record<string, string> = {
    sell: '低于卖出线',
    below_new_buy: '低于新买入门槛',
    hold_or_buy: '可持有/买入',
    observe: '观察',
}

const RECOMMEND_CONFIG: Record<string, { label: string; color: string }> = {
    buy:   { label: '买入', color: '#22c55e' },
    hold:  { label: '持有', color: '#eab308' },
    avoid: { label: '回避', color: '#ef4444' },
}

function ThinkingStepBody({ step, postGateCandidateCodes }: {
    step: ThinkingStep
    postGateCandidateCodes?: Set<string>
}) {
    const a = step.args ?? {}

    if (step.tool === 'screen_stocks') {
        const filters = a.filters as _FilterRow[] | undefined
        let rows: Array<{ code?: string; is_holding?: boolean }> = []
        let previewMeta: Record<string, unknown> = {}
        if (step.result_preview) {
            try {
                const parsed = JSON.parse(step.result_preview)
                previewMeta = parsed ?? {}
                rows = (parsed?.results ?? []) as Array<{ code?: string; is_holding?: boolean }>
            } catch {
                rows = [...step.result_preview.matchAll(/"code":\s*"([^"]+)"/g)]
                    .map(m => ({ code: m[1] }))
            }
        }
        const holdingCodes = rows.filter(row => row.is_holding === true).map(row => row.code).filter(Boolean) as string[]
        const candidateCodes = rows.filter(row => row.is_holding !== true).map(row => row.code).filter(Boolean) as string[]
        const hasHoldingFlag = rows.some(row => typeof row.is_holding === 'boolean')
        return (
            <div className="backtest-detail__thinking-step-body">
                {filters?.length ? (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">筛选条件</span>
                        <ul>{filters.filter(Boolean).map((f, i) => (
                            <li key={i}>
                                <code>{f.field ?? ''}</code>
                                {' '}{f.operator ?? ''}{' '}
                                <strong>{f.value != null ? String(f.value) : ''}</strong>
                            </li>
                        ))}</ul>
                    </div>
                ) : null}
                {(previewMeta.matched_count != null || previewMeta.returned_count != null) && (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">筛选漏斗</span>
                        <span>
                            硬筛命中 {String(previewMeta.matched_count ?? '--')} 只
                            {' → '}门禁后 {String(previewMeta.post_gate_count ?? previewMeta.returned_count ?? '--')} 只
                        </span>
                    </div>
                )}
                {candidateCodes.length > 0 && (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">
                            {hasHoldingFlag ? `可研究新候选（${candidateCodes.length} 只）` : `门禁后标的（${candidateCodes.length} 只）`}
                        </span>
                        <span>{candidateCodes.join(' · ')}</span>
                    </div>
                )}
                {holdingCodes.length > 0 && (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">同步复核持仓（{holdingCodes.length} 只）</span>
                        <span>{holdingCodes.join(' · ')}</span>
                    </div>
                )}
            </div>
        )
    }

    if (step.tool === 'query_fundamentals') {
        const codes = (a.queried_codes ?? a.codes) as string[] | undefined
        const fields = a.fields as string[] | undefined
        const KEY_FIELDS = ['revenue_yoy_pct', 'gross_margin_pct', 'operating_margin_pct', 'pe_ttm_percentile_3y', 'fcf_ttm', 'market_cap']
        const shown = fields ? KEY_FIELDS.filter(f => fields.includes(f)) : []
        const extra = fields ? fields.length - shown.length : 0
        return (
            <div className="backtest-detail__thinking-step-body">
                {codes?.length ? (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">查询标的</span>
                        <span>{codes.join(' · ')}</span>
                    </div>
                ) : null}
                {shown.length > 0 && (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">关键指标</span>
                        <span>{shown.join('、')}{extra > 0 ? `　等共 ${fields!.length} 项` : ''}</span>
                    </div>
                )}
            </div>
        )
    }

    if (step.tool === 'analyze_candidates') {
        const actualCandidateCodes = (a.candidate_codes ?? a.requested_codes ?? a.codes) as string[] | undefined
        const holdingCodes = a.holding_supplement_codes as string[] | undefined
        const rejectedCodes = a.rejected_requested_codes as string[] | undefined
        const total = a.unique_research_count as number | undefined
        const focus = a.focus as string | undefined
        const outOfPoolCodes = postGateCandidateCodes
            ? (actualCandidateCodes ?? []).filter(code => !postGateCandidateCodes.has(code))
            : []
        const candidateCodes = outOfPoolCodes.length > 0
            ? (actualCandidateCodes ?? []).filter(code => postGateCandidateCodes?.has(code))
            : actualCandidateCodes
        return (
            <div className="backtest-detail__thinking-step-body">
                {focus && (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">研究方向</span>
                        <span>{focus}</span>
                    </div>
                )}
                {total != null && (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">实际研究总数</span>
                        <span>
                            {total} 只（门禁后新候选 {candidateCodes?.length ?? 0} · 持仓复核 {holdingCodes?.length ?? 0}
                            {outOfPoolCodes.length > 0 ? ` · 历史越界研究 ${outOfPoolCodes.length}` : ''}）
                        </span>
                    </div>
                )}
                {candidateCodes?.length ? (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">新候选研究（{candidateCodes.length} 只）</span>
                        <span>{candidateCodes.join(' · ')}</span>
                    </div>
                ) : null}
                {holdingCodes?.length ? (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">持仓复核（{holdingCodes.length} 只）</span>
                        <span>{holdingCodes.join(' · ')}</span>
                    </div>
                ) : null}
                {rejectedCodes?.length ? (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">门禁拦截（{rejectedCodes.length} 只）</span>
                        <span>{rejectedCodes.join(' · ')}（未进入研究）</span>
                    </div>
                ) : null}
                {outOfPoolCodes.length > 0 && (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">历史越界研究（{outOfPoolCodes.length} 只）</span>
                        <span>{outOfPoolCodes.join(' · ')}（旧流程曾绕过门禁，仅保留为历史审计）</span>
                    </div>
                )}
            </div>
        )
    }

    if (step.tool === 'submit_portfolio_decision') {
        const orders = a.orders as _OrderRow2[] | undefined
        let result: string | null = null
        if (step.result_preview) {
            try {
                const p = JSON.parse(step.result_preview)
                if (typeof p === 'string') result = p
            } catch { /* ignore */ }
        }
        return (
            <div className="backtest-detail__thinking-step-body">
                {result && <div className="backtest-detail__thinking-result">{result}</div>}
                {orders?.length ? (
                    <div className="backtest-detail__thinking-group">
                        <span className="backtest-detail__thinking-group-title">目标组合提案</span>
                        <ul>{orders.map((o, i) => (
                            <li key={i}>
                                <code>{o.code}</code>
                                <strong>　→ {(Number(o.target_weight) * 100).toFixed(2)}%</strong>
                                {o.reason && <em>　{o.reason.length > 60 ? `${o.reason.slice(0, 60)}…` : o.reason}</em>}
                            </li>
                        ))}</ul>
                    </div>
                ) : null}
            </div>
        )
    }

    if (step.result_preview) {
        try {
            const p = JSON.parse(step.result_preview)
            if (typeof p === 'string') return <div className="backtest-detail__thinking-step-body">{p}</div>
        } catch { /* ignore */ }
    }
    return null
}

function DecisionSteps({ steps }: { steps: ThinkingStep[] }) {
    if (!steps.length) return null
    let postGateCandidateCodes: Set<string> | undefined
    const screenStep = [...steps].reverse().find(step => step.tool === 'screen_stocks' && step.result_preview)
    if (screenStep?.result_preview) {
        try {
            const parsed = JSON.parse(screenStep.result_preview)
            postGateCandidateCodes = new Set(
                (parsed?.results ?? [])
                    .filter((row: { is_holding?: boolean }) => row.is_holding !== true)
                    .map((row: { code?: string }) => row.code)
                    .filter(Boolean),
            )
        } catch { /* legacy truncated preview: skip cross-step comparison */ }
    }
    return (
        <div className="backtest-detail__thinking-section">
            <div className="backtest-detail__thinking-section-title">决策步骤</div>
            <ol className="backtest-detail__thinking-steps">
                {steps.map(s => (
                    <li key={s.step} className="backtest-detail__thinking-step">
                        <span className="backtest-detail__thinking-step-label">
                            {TOOL_LABELS[s.tool] ?? s.tool}
                        </span>
                        <ThinkingStepBody step={s} postGateCandidateCodes={postGateCandidateCodes} />
                    </li>
                ))}
            </ol>
        </div>
    )
}

function ScreeningSection({ screening, scoredCount }: { screening: ScreeningDetail; scoredCount?: number }) {
    const hasFilters = (screening.per_filter_counts?.length ?? 0) > 0
    const hasInvalid = (screening.invalid_filters?.length ?? 0) > 0
    const hasMeta = screening.total_universe != null || screening.matched_count != null
    const postGate = screening.post_gate_count ?? screening.returned_count ?? 0
    // 新数据直接携带筛选漏斗；旧记录退回 matched/returned，再退回评分数量估算。
    const gatedOut = screening.excluded_after_match_count
        ?? (screening.matched_count != null && screening.matched_count > postGate
            ? screening.matched_count - postGate
            : scoredCount != null && scoredCount > postGate ? scoredCount - postGate : 0)
    if (!hasFilters && !hasInvalid && !hasMeta) return null
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">筛选明细</div>
            {hasMeta && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">全市场</span>
                    <span>
                        {screening.total_universe ?? '--'} 只
                        {' → '}硬筛命中 {screening.matched_count ?? '--'} 只
                        {' → '}门禁后 {postGate} 只
                        {screening.screening_mode ? `（${screening.screening_mode}）` : ''}
                    </span>
                </div>
            )}
            {gatedOut > 0 && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">门禁剔除</span>
                    <span>
                        硬筛命中后另有 {gatedOut} 只因新买入门槛、数据完整度、财报时效或候选池上限未进入研究池
                    </span>
                </div>
            )}
            {(screening.eligible_candidate_count != null || screening.retained_holding_count != null) && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">门禁后构成</span>
                    <span>
                        可研究新候选 {screening.eligible_candidate_count ?? 0} 只
                        {' · '}硬筛内持仓 {screening.retained_holding_count ?? 0} 只
                    </span>
                </div>
            )}
            {hasFilters && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">逐条筛选命中数</span>
                    <ul>
                        {screening.per_filter_counts!.map((f, i) => (
                            <li key={i}>
                                <code>{f.field ?? ''}</code>
                                {' '}{f.operator ?? ''}{' '}
                                <strong>{f.value != null ? String(f.value) : ''}</strong>
                                <span>　命中 {f.matched ?? 0} 只</span>
                                {f.missing != null && (
                                    <span className="asset-thinking__filter-missing">　缺数据 {f.missing} 只</span>
                                )}
                                {f.invalid && (
                                    <span className="asset-thinking__filter-invalid">　无效字段</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {hasInvalid && (
                <div className="asset-thinking__step-group">
                    <span className="asset-thinking__step-group-title">无效筛选</span>
                    <ul>
                        {screening.invalid_filters!.map((f, i) => (
                            <li key={i}>
                                <code>{f.field ?? ''}</code>
                                {f.reason ? `（${f.reason}）` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

function DeterministicScoreSection({ scores, minNewBuy }: { scores: Record<string, DeterministicScore>; minNewBuy?: number }) {
    const entries = Object.entries(scores ?? {})
    if (!entries.length) return null
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">
                确定性评分{minNewBuy != null ? `（新买入门槛 ${Math.round(minNewBuy)} 分）` : ''}
            </div>
            <div className="asset-thinking__score-list">
                {entries.map(([code, s]) => {
                    const below = s.complete === true && minNewBuy != null && s.total_score != null && s.total_score < minNewBuy
                    return (
                        <div key={code} className={`asset-thinking__score-item ${s.hard_excluded ? 'is-excluded' : ''}`}>
                            <div className="asset-thinking__score-head">
                                <code>{code}</code>
                                <strong className={below ? 'is-below' : ''}>
                                    {s.total_score != null ? `${s.total_score} 分` : '-- 分'}
                                </strong>
                                {s.complete === false && <span className="asset-thinking__score-incomplete">数据不全</span>}
                                {s.hard_excluded && <span className="asset-thinking__score-excluded">硬性剔除</span>}
                                {s.report_stale && <span className="asset-thinking__score-stale">财报陈旧</span>}
                                {s.hard_filter_missing_fields && s.hard_filter_missing_fields.length > 0 && (
                                    <span className="asset-thinking__score-missing-filter">缺数据过筛</span>
                                )}
                                {s.action_bucket && (
                                    <span className="asset-thinking__score-bucket">
                                        {ACTION_BUCKET_LABELS[s.action_bucket] ?? s.action_bucket}
                                    </span>
                                )}
                            </div>
                            {s.subscores && Object.keys(s.subscores).length > 0 && (
                                <div className="asset-thinking__score-subs">
                                    {Object.entries(s.subscores).map(([k, v]) => (
                                        <span key={k}>{SUBSCORE_LABELS[k] ?? k} {v != null ? v : '--'}</span>
                                    ))}
                                </div>
                            )}
                            {s.hard_exclusion_reasons && s.hard_exclusion_reasons.length > 0 && (
                                <div className="asset-thinking__score-reasons">
                                    剔除原因：{s.hard_exclusion_reasons.join('；')}
                                </div>
                            )}
                            {s.hard_filter_missing_fields && s.hard_filter_missing_fields.length > 0 && (
                                <div className="asset-thinking__score-missing-filter-detail">
                                    缺数据过筛字段：{s.hard_filter_missing_fields.join('、')}
                                </div>
                            )}
                            {s.warnings && s.warnings.length > 0 && (
                                <div className="asset-thinking__score-warnings">
                                    提示：{s.warnings.join('；')}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function StockAnalysisSection({ reports }: { reports: StockReport[] }) {
    if (!reports.length) return null
    return (
        <div className="asset-thinking__section">
            <div className="asset-thinking__section-title">候选股研究（{reports.length} 只）</div>
            <Collapse
                size="small"
                className="asset-thinking__analysis"
                items={reports.map(r => {
                    const rec = RECOMMEND_CONFIG[r.recommendation] ?? { label: r.recommendation, color: '#64748b' }
                    const confidencePct = `${Math.round(r.confidence * 100)}%`
                    return {
                        key: r.code,
                        label: (
                            <span className="asset-thinking__analysis-header">
                                <span
                                    className="asset-thinking__analysis-rec"
                                    style={{ background: `${rec.color}20`, color: rec.color }}
                                >
                                    {rec.label}
                                </span>
                                <code className="asset-thinking__analysis-code">{r.code}</code>
                                <span className="asset-thinking__analysis-confidence">置信度 {confidencePct}</span>
                                {r.expected_holding_days != null && (
                                    <span className="asset-thinking__analysis-days">预计持有 {r.expected_holding_days} 天</span>
                                )}
                            </span>
                        ),
                        children: (
                            <div className="asset-thinking__analysis-body">
                                <p className="asset-thinking__analysis-thesis">{r.thesis}</p>
                                {r.evidence && r.evidence.length > 0 && (
                                    <div className="asset-thinking__analysis-list">
                                        <div className="asset-thinking__analysis-list-title">支撑论据</div>
                                        <ul>{r.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                    </div>
                                )}
                                {r.risks && r.risks.length > 0 && (
                                    <div className="asset-thinking__analysis-list">
                                        <div className="asset-thinking__analysis-list-title">主要风险</div>
                                        <ul>{r.risks.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                    </div>
                                )}
                                {r.invalidation_conditions && r.invalidation_conditions.length > 0 && (
                                    <div className="asset-thinking__analysis-list">
                                        <div className="asset-thinking__analysis-list-title">失效条件</div>
                                        <ul>{r.invalidation_conditions.map((e, i) => <li key={i}>{e}</li>)}</ul>
                                    </div>
                                )}
                            </div>
                        ),
                    }
                })}
            />
        </div>
    )
}

function ReportStaleSection({ codes }: { codes: string[] }) {
    if (!codes?.length) return null
    return (
        <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(245, 158, 11, 0.5)' }}>
            <div className="asset-thinking__section-title">财报陈旧剔除</div>
            <div className="asset-thinking__step-group">
                <span className="asset-thinking__step-group-title">剔除标的（{codes.length} 只）</span>
                <span>{codes.join(' · ')}</span>
            </div>
        </div>
    )
}

function DataInsufficientSection({ codes }: { codes: string[] }) {
    if (!codes?.length) return null
    return (
        <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(239, 68, 68, 0.5)' }}>
            <div className="asset-thinking__section-title">数据完整度不足剔除</div>
            <div className="asset-thinking__step-group">
                <span className="asset-thinking__step-group-title">剔除标的（{codes.length} 只）</span>
                <span>{codes.join(' · ')}</span>
            </div>
            <div className="asset-thinking__step-group">
                <span>这些标的未通过策略的数据完整度门槛，已排除在候选池与个股研究之外，不得新建仓；数据补全后可重新评估。</span>
            </div>
        </div>
    )
}

function NewBuyIneligibleSection({ codes }: { codes: string[] }) {
    if (!codes?.length) return null
    return (
        <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(100, 116, 139, 0.5)' }}>
            <div className="asset-thinking__section-title">新建仓资格剔除</div>
            <div className="asset-thinking__step-group">
                <span className="asset-thinking__step-group-title">未进入研究池（{codes.length} 只）</span>
                <span>{codes.join(' · ')}</span>
            </div>
            <div className="asset-thinking__step-group">
                <span>这些标的通过了初始硬筛，但未通过分数线或策略的新买入附加门槛，仅保留评分审计。</span>
            </div>
        </div>
    )
}

function DecisionOverview({ detail, trades }: { detail: AISignal; trades: BacktestTradesDetail[] }) {
    const steps = (detail.thinking_chain ?? []) as ThinkingStep[]
    const finalOrders = (detail.orders ?? []) as _OrderRow2[]
    const proposal = latestProposalOrders(steps)
    const proposedOrders = proposal.length ? proposal : finalOrders
    const adjustments = decisionAdjustments(proposedOrders, finalOrders)
    const decisionOutcome = detail.decision_outcome
        ?? (detail.trigger_detail?.decision_outcome as string | undefined)
    const outcome = DECISION_OUTCOME_LABELS[decisionOutcome ?? '']
        ?? { label: decisionOutcome || '状态未知', color: 'default' }
    const positiveTargets = finalOrders.filter(order => Number(order.target_weight) > 1e-9)

    return (
        <div className="backtest-detail__decision-overview">
            <div className="backtest-detail__decision-overview-head">
                <div>
                    <strong>{signalEventLabel(detail)}</strong>
                    <span>{detail.signal_date}</span>
                </div>
                <div>
                    <Tag color={outcome.color}>{outcome.label}</Tag>
                    <Tag color={trades.length ? 'processing' : 'default'}>
                        {trades.length ? `实际成交 ${trades.length} 笔` : '未产生成交'}
                    </Tag>
                </div>
            </div>
            <div className="backtest-detail__decision-stages">
                <div className="backtest-detail__decision-stage">
                    <span>1 · AI原始提案</span>
                    <strong>{proposedOrders.filter(order => Number(order.target_weight) > 1e-9).length} 只</strong>
                    <small>股票权重 {targetWeightOf(proposedOrders).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 })}</small>
                </div>
                <div className="backtest-detail__decision-stage">
                    <span>2 · 系统门禁后目标</span>
                    <strong>{positiveTargets.length} 只</strong>
                    <small>
                        股票权重 {targetWeightOf(finalOrders).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 })}
                        {adjustments.length ? ` · 调整 ${adjustments.length} 只` : ' · 未调整'}
                    </small>
                </div>
                <div className="backtest-detail__decision-stage">
                    <span>3 · 实际成交</span>
                    <strong>{trades.length} 笔</strong>
                    <small>{trades.length ? [...new Set(trades.map(trade => trade.code))].join('、') : '该信号未关联成交记录'}</small>
                </div>
            </div>
            {adjustments.length > 0 && (
                <div className="backtest-detail__decision-adjustments">
                    <span>系统门禁调整</span>
                    {adjustments.map(item => (
                        <Tag key={item.code}>
                            {item.code} {(item.proposed * 100).toFixed(2)}% → {(item.final * 100).toFixed(2)}%
                        </Tag>
                    ))}
                </div>
            )}
            {trades.length > 0 && (
                <div className="backtest-detail__decision-fills">
                    {trades.map(trade => (
                        <span key={`${trade.sequence_no}-${trade.code}`}>
                            <b className={trade.side === 'BUY' ? 'red' : 'green'}>
                                {trade.side === 'BUY' ? '买入' : '卖出'}
                            </b>
                            {' '}{trade.code} · {trade.shares} 股 @ {Number(trade.price).toFixed(2)} · {trade.date}
                        </span>
                    ))}
                </div>
            )}
            <p className="backtest-detail__decision-note">
                目标组合不是成交订单；是否真实交易以“实际成交”及交易记录为准。
            </p>
        </div>
    )
}

function AISignalDetailBody({ detail, trades = [] }: { detail: AISignal; trades?: BacktestTradesDetail[] }) {
    const steps = (detail.thinking_chain ?? []) as ThinkingStep[]
    const reports = (detail.stock_analysis ?? []) as StockReport[]
    const triggerDetail = detail.trigger_detail as Record<string, unknown> | undefined
    const screening = triggerDetail?.screening as ScreeningDetail | undefined
    const detScores = triggerDetail?.deterministic_scores as Record<string, DeterministicScore> | undefined
    const minNewBuy = triggerDetail?.min_new_buy_score as number | undefined
    const drift = triggerDetail?.scoring_model_drift as string[] | undefined
    const staleCodes = (triggerDetail?.report_stale_codes as string[] | undefined) ?? []
    const insufficientCodes = (triggerDetail?.data_insufficient_codes as string[] | undefined) ?? []
    const ineligibleCodes = (triggerDetail?.new_buy_ineligible_codes as string[] | undefined) ?? []
    const auditItems = [
        steps.length > 0 ? {
            key: 'steps',
            label: `AI工具过程（${steps.length} 步）`,
            children: <DecisionSteps steps={steps} />,
        } : null,
        screening ? {
            key: 'screening',
            label: '完整筛选明细',
            children: <ScreeningSection screening={screening} scoredCount={detScores ? Object.keys(detScores).length : undefined} />,
        } : null,
        detScores ? {
            key: 'scores',
            label: `全部确定性评分（${Object.keys(detScores).length} 只）`,
            children: <DeterministicScoreSection scores={detScores} minNewBuy={minNewBuy} />,
        } : null,
        reports.length > 0 ? {
            key: 'reports',
            label: `全部候选股研究（${reports.length} 只）`,
            children: <StockAnalysisSection reports={reports} />,
        } : null,
    ].filter(Boolean)
    return (
        <div className="backtest-detail__ai-signal-item-content">
            <DecisionOverview detail={detail} trades={trades} />
            {detail.ai_summary && (
                <Collapse
                    className="backtest-detail__decision-detail-collapse"
                    defaultActiveKey={['summary']}
                    items={[{ key: 'summary', label: 'AI决策说明（原始记录）', children: <MdViewer content={detail.ai_summary} /> }]}
                />
            )}
            {staleCodes.length > 0 && <ReportStaleSection codes={staleCodes} />}
            {insufficientCodes.length > 0 && <DataInsufficientSection codes={insufficientCodes} />}
            {ineligibleCodes.length > 0 && <NewBuyIneligibleSection codes={ineligibleCodes} />}
            {drift && drift.length > 0 && (
                <div className="asset-thinking__section" style={{ borderLeftColor: 'rgba(239, 68, 68, 0.4)' }}>
                    <div className="asset-thinking__section-title">评分执行参数漂移告警</div>
                    <div className="asset-thinking__step-group">
                        <span>分数线、仓位或换手等执行参数与审核规则不一致；系统已回退确定性规则，请核对：</span>
                        <ul>{drift.map((d, i) => <li key={i}>{d}</li>)}</ul>
                    </div>
                </div>
            )}

            {!detail.ai_summary && detail.orders?.length ? (
                <div>最终目标组合：{JSON.stringify(detail.orders)}</div>
            ) : null}
            {!detail.ai_summary && detail.signal_type === 'STOP_LOSS' && detail.trigger_detail ? (
                <div className="backtest-detail__ai-signal-item-content-stop-loss">
                    触发详情：{JSON.stringify(detail.trigger_detail)}
                </div>
            ) : null}
            {auditItems.length > 0 && (
                <Collapse
                    className="backtest-detail__decision-detail-collapse"
                    items={auditItems as Array<{ key: string; label: string; children: ReactNode }>}
                />
            )}
        </div>
    )
}

function AISignalsItem({
    backtestId,
    signal,
    trades,
}: {
    backtestId: number
    signal: AISignalSummary
    trades: BacktestTradesDetail[]
}) {
    const [detail, setDetail] = useState<AISignal | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const signalTrades = trades.filter(trade => trade.signal_id === signal.id)
    const outcome = DECISION_OUTCOME_LABELS[signal.decision_outcome ?? '']
        ?? { label: signal.decision_outcome || signal.status, color: 'default' }

    const loadDetail = async () => {
        if (detail || detailLoading) {
            return
        }
        setDetailLoading(true)
        try {
            const res = await getAISignalDetail(backtestId, signal.id)
            setDetail(res.item)
        } catch {
            message.error('加载信号详情失败')
        } finally {
            setDetailLoading(false)
        }
    }

    return (
        <Collapse
            className="backtest-detail__ai-signal-item"
            items={[
                {
                    key: String(signal.id),
                    label: (
                        <div className="backtest-detail__ai-signal-item-header">
                            <span className="backtest-detail__ai-signal-item-title">
                                {signal.signal_date}
                                <Tag color="blue" className="text-10" style={{ marginLeft: 8 }}>
                                    {signalEventLabel(signal)}
                                </Tag>
                                <Tag color={outcome.color} className="text-10">{outcome.label}</Tag>
                                {signal.stock_code ? (
                                    <span className="backtest-detail__ai-signal-item-code">{signal.stock_code}</span>
                                ) : null}
                            </span>
                            <div className="backtest-detail__ai-signal-item-facts">
                                <span>目标持仓 {signal.target_position_count ?? '--'} 只</span>
                                {signal.target_weight != null && <span>股票权重 {(signal.target_weight * 100).toFixed(2)}%</span>}
                                <span>{signalTrades.length ? `实际成交 ${signalTrades.length} 笔` : '未产生成交'}</span>
                            </div>
                            {signal.ai_summary_preview ? (
                                <p className="backtest-detail__ai-signal-item-preview">{signal.ai_summary_preview}</p>
                            ) : signal.has_full_summary ? (
                                <p className="backtest-detail__ai-signal-item-preview is-muted">点击展开查看详情</p>
                            ) : null}
                        </div>
                    ),
                    children: detailLoading ? (
                        <div style={{ padding: '16px 0', textAlign: 'center' }}>
                            <Spin size="small" />
                        </div>
                    ) : detail ? (
                        <AISignalDetailBody detail={detail} trades={signalTrades} />
                    ) : null,
                },
            ]}
            onChange={(keys) => {
                if (keys.length > 0) {
                    void loadDetail()
                }
            }}
        />
    )
}

export default function Detail() {
    const [activeTab, setActiveTab] = useState<DetailTabKey>('returns')
    const { run_id } = useParams<{ run_id: string }>()
    const [backtestDetail, setBacktestDetail] = useState<BacktestDeatil | null>(null)
    const [strategyNavItems, setStrategyNavItems] = useState<NavPoint[]>([])
    const [navRange, setNavRange] = useState<[string, string] | null>(null)
    const [benchmarkByDate, setBenchmarkByDate] = useState<Map<string, number>>(new Map())
    const [poolByDate, setPoolByDate] = useState<Map<string, number>>(new Map())
    const [poolLegend, setPoolLegend] = useState<string | null>(null)
    const [selectedBenchmark, setSelectedBenchmark] = useState<string>(DEFAULT_BENCHMARK)
    const [benchmarkLoading, setBenchmarkLoading] = useState(false)
    const [showBenchmark, setShowBenchmark] = useState(false)
    const [holdingsDate, setHoldingsDate] = useState<string | null>(null)
    const [holdingsLoading, setHoldingsLoading] = useState(false)
    const [holdingsSnapshot, setHoldingsSnapshot] = useState<HoldingsSnapshot | null>(null)
    const [backtestTradesDetail, setBacktestTradesDetail] = useState<BacktestTradesDetail[] | null>(null)
    const [backtestNavDrawdownDetail, setBacktestNavDrawdownDetail] = useState<NavPoint[] | null>(null)
    const [backtestReasonDetail, setBacktestReasonDetail] = useState<BacktestReasonDetail[] | []>([])
    const [reasonSummary, setReasonSummary] = useState<{
        open_position_count: number
        open_unrealized_pnl: number
    }>({ open_position_count: 0, open_unrealized_pnl: 0 })
    const [loading, setLoading] = useState(true)
    const [signalsLoading, setSignalsLoading] = useState(false)
    const [aiSignalsKeys, setAiSignalsKeys] = useState<aiSignalsKeys>('fundamental')
    const [fundamentalSignals, setFundamentalSignals] = useState<AISignalSummary[]>([])
    const [klineSignals, setKlineSignals] = useState<AISignalSummary[]>([])
    const [reportSignals, setReportSignals] = useState<AISignalSummary[]>([])
    const [signalsMeta, setSignalsMeta] = useState<Record<aiSignalsKeys, { total: number; page: number }>>({
        fundamental: { total: 0, page: 1 },
        kline: { total: 0, page: 1 },
        report: { total: 0, page: 1 },
    })
    const [signalModalOpen, setSignalModalOpen] = useState(false)
    const [signalModalLoading, setSignalModalLoading] = useState(false)
    const [signalModalDetail, setSignalModalDetail] = useState<AISignal | null>(null)

    const navigate = useNavigate()

    // 交易记录 → 决策信号：点击「信号原因」打开弹窗加载该笔成交对应的信号详情
    const openSignalModal = useCallback(async (signalId: number) => {
        if (!backtestDetail?.id) return
        setSignalModalOpen(true)
        setSignalModalLoading(true)
        setSignalModalDetail(null)
        try {
            const res = await getAISignalDetail(backtestDetail.id, signalId)
            setSignalModalDetail(res.item)
        } catch {
            message.error('加载信号详情失败')
        } finally {
            setSignalModalLoading(false)
        }
    }, [backtestDetail?.id])

    const columns: ColumnsType<BacktestTradesDetail> = useMemo(() => [
        {
            title: '日期',
            dataIndex: 'date',
            key: 'date',
            width: 160,
        },
        {
            title: '操作',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (text: string) => <span style={{ fontWeight: 600 }} className={`${text === '买入' ? 'red' : 'green'}`}>{text}</span>
        },
        {
            title: '股票',
            dataIndex: 'code',
            key: 'code',
            width: 150,
            align: 'right',
        },
        {
            title: '数量',
            dataIndex: 'shares',
            key: 'shares',
            width: 120,
            align: 'center',
        },
        {
            title: '价格',
            dataIndex: 'price',
            key: 'price',
            width: 120,
            render: (text: number) => <span>￥{filterFormatWanYiNumber(text)}</span>
        },
        {
            title: '盈亏',
            dataIndex: 'pnl_pct',
            key: 'pnl_pct',
            width: 120,
            render: (text: number) => <span>{text ? (text >= 0 ? '+' + filterFormatWanYiNumber(text) : filterFormatWanYiNumber(text)) + '%' : '—'}</span>
        },
        {
            title: '信号原因',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
        },
        {
            title: '决策',
            key: 'signal',
            width: 100,
            align: 'center',
            render: (_text: string, record: BacktestTradesDetail) => {
                if (record.signal_id == null) return <span style={{ color: 'var(--text-3, #94a3b8)' }}>—</span>
                return (
                    <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={(e) => {
                            e.stopPropagation()
                            void openSignalModal(record.signal_id as number)
                        }}
                    >
                        查看决策
                    </Button>
                )
            }
        },
    ], [openSignalModal])

    const holdingsColumns: ColumnsType<HoldingsSnapshot['items'][number]> = useMemo(() => [
        { title: '股票', dataIndex: 'code', key: 'code', width: 140 },
        { title: '名称', dataIndex: 'name', key: 'name', width: 140 },
        {
            title: '股数',
            dataIndex: 'shares',
            key: 'shares',
            width: 110,
            align: 'right',
            render: (value: number) => filterFormatThousandNumber(value, Number.isInteger(value) ? 0 : 2),
        },
        {
            title: '成本价',
            dataIndex: 'cost_price',
            key: 'cost_price',
            width: 110,
            render: (value: number | null) => value == null ? '—' : `￥${filterFormatWanYiNumber(value)}`,
        },
        {
            title: '收盘价',
            dataIndex: 'price',
            key: 'price',
            width: 110,
            render: (value: number | null, record) => value == null
                ? '—'
                : `￥${filterFormatWanYiNumber(value)}${record.price_date && record.price_date !== holdingsDate ? ` (${record.price_date})` : ''}`,
        },
        {
            title: '市值',
            dataIndex: 'market_value',
            key: 'market_value',
            width: 120,
            render: (value: number | null) => value == null ? '—' : `￥${filterFormatWanYiNumber(value)}`,
        },
        {
            title: '权重',
            dataIndex: 'weight_pct',
            key: 'weight_pct',
            width: 90,
            render: (value: number | null) => value == null ? '—' : `${filterFormatThousandNumber(value)}%`,
        },
        {
            title: '浮盈',
            dataIndex: 'unrealized_pnl',
            key: 'unrealized_pnl',
            width: 140,
            render: (value: number | null, record) => {
                if (value == null) return '—'
                const tone = value > 0 ? 'red' : value < 0 ? 'green' : ''
                const pct = record.unrealized_pnl_pct
                return (
                    <span className={tone}>
                        {value > 0 ? '+' : ''}{filterFormatWanYiNumber(value)}
                        {pct != null ? ` (${pct > 0 ? '+' : ''}${filterFormatThousandNumber(pct)}%)` : ''}
                    </span>
                )
            },
        },
    ], [holdingsDate])

    const chartNav = useMemo(() => strategyNavItems.map((item) => ({
        ...item,
        benchmark_nav: benchmarkByDate.get(tradeDateText(item.trade_date)),
        pool_nav: poolByDate.get(tradeDateText(item.trade_date)),
    })), [strategyNavItems, benchmarkByDate, poolByDate])

    const navDateBounds = useMemo(() => {
        if (!strategyNavItems.length) {
            return null
        }
        const dates = strategyNavItems.map((item) => tradeDateText(item.trade_date)).sort()
        return { start: dates[0], end: dates[dates.length - 1] }
    }, [strategyNavItems])

    const yearBounds = useMemo(() => {
        const bounds = new Map<string, [string, string]>()
        strategyNavItems.forEach((item) => {
            const date = tradeDateText(item.trade_date)
            const year = date.slice(0, 4)
            const current = bounds.get(year)
            if (!current) {
                bounds.set(year, [date, date])
                return
            }
            if (date < current[0]) current[0] = date
            if (date > current[1]) current[1] = date
        })
        return bounds
    }, [strategyNavItems])

    const selectedNavYear = useMemo(() => {
        if (!navRange || !navDateBounds) {
            return 'all'
        }
        for (const [year, bounds] of yearBounds) {
            if (bounds[0] === navRange[0] && bounds[1] === navRange[1]) {
                return year
            }
        }
        if (navRange[0] === navDateBounds.start && navRange[1] === navDateBounds.end) {
            return 'all'
        }
        return 'custom'
    }, [navRange, navDateBounds, yearBounds])

    const yearOptions = useMemo(() => {
        const options: Array<{ label: string; value: string; disabled?: boolean }> = [
            { label: '全部年份', value: 'all' },
            ...[...yearBounds.keys()].sort().map((year) => ({ label: `${year}年`, value: year })),
        ]
        if (selectedNavYear === 'custom') {
            options.push({ label: '自定义', value: 'custom', disabled: true })
        }
        return options
    }, [yearBounds, selectedNavYear])

    const isNavSubWindow = Boolean(
        navRange && navDateBounds && (navRange[0] > navDateBounds.start || navRange[1] < navDateBounds.end),
    )

    const visibleChartNav = useMemo(() => {
        const sliced = chartNav.filter((item) => inSelectedRange(item.trade_date, navRange))
        return isNavSubWindow ? rebaseToWindowStart(sliced) : sliced
    }, [chartNav, navRange, isNavSubWindow])

    const positionSeries = useMemo(() => (
        strategyNavItems.flatMap((item) => {
            const positionPct = investedPositionPct(item)
            if (positionPct == null) {
                return []
            }
            return [{ ...item, position_pct: positionPct }]
        })
    ), [strategyNavItems])

    const navRangeValue: [Dayjs, Dayjs] | null = navRange
        ? [dayjs(navRange[0]), dayjs(navRange[1])]
        : navDateBounds
            ? [dayjs(navDateBounds.start), dayjs(navDateBounds.end)]
            : null

    const changeNavYear = (year: string) => {
        if (year === 'all' || year === 'custom') {
            setNavRange(null)
            return
        }
        const bounds = yearBounds.get(year)
        setNavRange(bounds ?? null)
    }

    const loadBenchmarkNav = useCallback((backtestId: number, benchmark: string) => {
        setBenchmarkLoading(true)
        getBacktestBenchmarkNav(backtestId, benchmark)
            .then((result) => {
                const benchmarkMap = new Map(
                    result.items
                        .filter((item) => typeof item.nav_value === 'number')
                        .map((item) => [tradeDateText(item.trade_date), item.nav_value as number]),
                )
                const hasBenchmark = benchmarkMap.size > 0
                setShowBenchmark(hasBenchmark)
                setBenchmarkByDate(hasBenchmark ? benchmarkMap : new Map())
                if (hasBenchmark) {
                    setBacktestDetail((previous) => previous ? {
                        ...previous,
                        results: {
                            ...previous.results,
                            ...result.metrics,
                        },
                    } : previous)
                }
            })
            .catch(() => {
                setShowBenchmark(false)
                setBenchmarkByDate(new Map())
            })
            .finally(() => {
                setBenchmarkLoading(false)
            })
    }, [])

    useEffect(() => {
        if (!strategyNavItems.length || !backtestDetail?.id) {
            return
        }
        loadBenchmarkNav(backtestDetail.id, selectedBenchmark)
    }, [selectedBenchmark, strategyNavItems, backtestDetail?.id, loadBenchmarkNav])

    useEffect(() => {
        if (!run_id) {
            return
        }
        load()

    }, [run_id])

    const load = () => {
        if (!run_id) {
            return
        }
        setLoading(true)
        setNavRange(null)
        setPoolByDate(new Map())
        setPoolLegend(null)
        setHoldingsDate(null)
        setHoldingsSnapshot(null)
        Promise.all([getBacktestDetail(run_id), getBacktestNavDrawdownDetail(run_id)])
            .then(([data, navDrawdown]) => {
                setBacktestDetail({
                    ...data,
                    max_drawdown_days:
                        navDrawdown.summary?.max_drawdown_duration_days ?? data.max_drawdown_days,
                })
                setBacktestNavDrawdownDetail(navDrawdown.items as NavPoint[])
                if (!data.id) {
                    setLoading(false)
                    return
                }
                const defaultBenchmark = normalizeBenchmarkLabel(data.config?.benchmark)
                setSelectedBenchmark(defaultBenchmark)
                getBacktestNavDetail(data.id).then((result) => {
                    const navItems = result.items as NavPoint[]
                    setStrategyNavItems(navItems)
                })
                getBacktestStockPoolNav(data.id)
                    .then((result) => {
                        const poolMap = new Map(
                            result.items
                                .filter((item) => typeof item.nav_value === 'number')
                                .map((item) => [tradeDateText(item.trade_date), item.nav_value as number]),
                        )
                        if (!poolMap.size) {
                            setPoolByDate(new Map())
                            setPoolLegend(null)
                            return
                        }
                        const poolName = result.stock_pool_name?.trim()
                        setPoolLegend(poolName ? `${poolName}等权` : '股票池等权')
                        setPoolByDate(poolMap)
                    })
                    .catch(() => {
                        setPoolByDate(new Map())
                        setPoolLegend(null)
                    })
                getBacktestTradesDetail(data.id).then((result) => setBacktestTradesDetail(result.items as BacktestTradesDetail[]))
                getBacktestReasonDetail(data.id).then((res) => {
                    setBacktestReasonDetail(res.items as BacktestReasonDetail[])
                    setReasonSummary({
                        open_position_count: Number(res.open_position_count || 0),
                        open_unrealized_pnl: Number(res.open_unrealized_pnl || 0),
                    })
                }).finally(() => {
                    setLoading(false)
                })
            })
    }

    const getDetailValue = (key: MetricValueKey) => {
        if (!key) {
            return ''
        }
        if (!backtestDetail) {
            return undefined
        }

        if (key.startsWith('results.')) {
            const nestedKey = key.slice('results.'.length) as keyof BacktestDeatil['results']
            return backtestDetail.results?.[nestedKey]
        }

        if (key.startsWith('config.')) {
            const nestedKey = key.slice('config.'.length) as keyof BacktestDeatil['config']
            if (nestedKey === 'commission') {
                return backtestDetail.config?.[nestedKey] * 1000
            }
            return backtestDetail.config?.[nestedKey]
        }

        return backtestDetail[key as keyof BacktestDeatil]
    }

    const getMetricDisplayValue = (val: MetricValueKey): string | number => {

        const rawValue = getDetailValue(val)

        if (typeof rawValue === 'number') {
            return Number.isInteger(rawValue)
                ? rawValue
                : filterFormatThousandNumber(rawValue)
        }

        if (typeof rawValue === 'string') {
            return rawValue
        }

        return '--'
    }

    const getMetricToneClass = (item: MetricCard) => {
        if (!item.tone) {
            return ''
        }

        const rawValue =
            item.value
                ? Number(getDetailValue(item.value) ?? 0)
                : 0

        return `${rawValue > 0 ? 'red' : 'green'}`
    }

    const getSectionDisplayValue = (item: StatsSectionItem) => {

        if (!item.value) {
            return '--'
        }

        const rawValue = getDetailValue(item.value)
        if (typeof rawValue === 'number') {
            return item.prefix ? filterFormatWanYiNumber(rawValue, 2) : Number.isInteger(rawValue)
                ? rawValue : filterFormatThousandNumber(rawValue)
        }

        return rawValue == null ? '--' : String(rawValue)
    }

    const getSectionClass = (item: StatsSectionItem) => {

        if (item.orderColor) {
            return `${item.orderColor}`
        }

        if (!item.tone) {
            return ''
        }
        const rawValue =
            item.value
                ? Number(getDetailValue(item.value) ?? 0)
                : 0

        return `${rawValue > 0 ? 'red' : 'green'}`
    }

    const handleArchive = () => {
        archiveBacktest(backtestDetail?.id ?? 0, backtestDetail?.record_status === 1 ? 0 : 1)
            .then(() => {
                message.success('操作成功')
                setBacktestDetail((prev) => prev ? { ...prev, record_status: backtestDetail?.record_status === 1 ? 0 : 1 } : prev)
            })
            .catch(() => {
                message.error('操作失败')
            })
    }

    const handleDelete = () => {
        deleteBacktest(backtestDetail?.id ?? 0)
            .then(() => {
                message.success('操作成功')
                navigate('/backtests')
            })
            .catch(() => {
                message.error('删除失败')
            })
    }

    //回测配置
    const runtimeConfigItems = useMemo(() => ([
        { label: '回测区间', value: `${backtestDetail?.backtest_range_start ?? '--'} ~ ${backtestDetail?.backtest_range_end ?? '--'}` },
        { label: '市场', value: `${backtestDetail?.config.market ?? '--'}` },
        { label: 'K线级别', value: backtestDetail?.config.freq ?? '--' },
        { label: '初始资金', value: `¥${filterFormatWanYiNumber(backtestDetail?.config.initialCapital)}` },
        { label: '基准指数', value: backtestDetail?.config.benchmark ?? '--' },
        { label: '手续费率', value: backtestDetail?.config.commission != null ? `${backtestDetail.config.commission}%` : '--' },
        { label: '滑点', value: backtestDetail?.config.slippage != null ? `${backtestDetail.config.slippage}%` : '--' },
        { label: '最大持仓数', value: backtestDetail?.config.maxPositions ?? '--' },
        { label: '监控信号', value: backtestDetail?.config.useMonitorSignals ? '已启用' : '已禁用' },
    ]), [backtestDetail])

    /*** 基本面：REBALANCE,REPORT_PUBLISHED
     * K线：STOP_LOSS,TAKE_PROFIT,PE_CEILING
     * 研报：RESEARCH_UPDATE
     **/
    const setSignalsForTab = (key: aiSignalsKeys, items: AISignalSummary[]) => {
        if (key === 'fundamental') setFundamentalSignals(items)
        else if (key === 'kline') setKlineSignals(items)
        else setReportSignals(items)
    }

    const loadAISignals = async (key: aiSignalsKeys, page = 1) => {
        const backtestId = backtestDetail?.id
        if (!backtestId) {
            return
        }
        setSignalsLoading(true)
        try {
            const res = await getAISignals({
                backtest_id: backtestId,
                signal_type: SIGNAL_TYPES[key],
                limit: SIGNALS_PAGE_SIZE,
                offset: (page - 1) * SIGNALS_PAGE_SIZE,
            })
            setSignalsForTab(key, res.items)
            setSignalsMeta((prev) => ({
                ...prev,
                [key]: { total: res.total, page },
            }))
        } finally {
            setSignalsLoading(false)
        }
    }

    const loadHoldings = useCallback(async (asOf: string) => {
        const backtestId = backtestDetail?.id
        if (!backtestId || !asOf) {
            return
        }
        setHoldingsLoading(true)
        try {
            const res = await getBacktestHoldings(backtestId, asOf)
            setHoldingsSnapshot(res)
        } catch (err) {
            setHoldingsSnapshot(null)
            message.error(err instanceof Error ? err.message : '查询持仓失败')
        } finally {
            setHoldingsLoading(false)
        }
    }, [backtestDetail?.id])

    useEffect(() => {
        if (activeTab !== 'holdings' || holdingsDate || !backtestDetail?.id) {
            return
        }
        const end = (backtestDetail.backtest_range_end || '').slice(0, 10)
        if (!end) {
            return
        }
        setHoldingsDate(end)
        void loadHoldings(end)
    }, [activeTab, holdingsDate, backtestDetail?.id, backtestDetail?.backtest_range_end, loadHoldings])

    const changeTab = async (key: DetailTabKey) => {
        setActiveTab(key)
        if (key === 'ai_signals') {
            setAiSignalsKeys('fundamental')
            await loadAISignals('fundamental', 1)
        }
        if (key === 'holdings' && !holdingsDate) {
            const end = (backtestDetail?.backtest_range_end || '').slice(0, 10)
            if (end) {
                setHoldingsDate(end)
                await loadHoldings(end)
            }
        }
    }

    const changeAiSignals = async (key: aiSignalsKeys) => {
        setAiSignalsKeys(key)
        await loadAISignals(key, 1)
    }

    const changeSignalsPage = (page: number) => {
        void loadAISignals(aiSignalsKeys, page)
    }

    return (
        <div className="backtest-detail">
            <div className="backtest-detail__topbar page-header">
                <button type="button" className="backtest-detail__back-button ant-btn-text" onClick={() => navigate('/backtests')}>
                    <span className="backtest-detail__back-icon">←</span>
                    回测列表
                </button>/
                <div className="backtest-detail__headline">
                    <span className="backtest-detail__name">{backtestDetail?.display_name ?? '--'}</span>
                </div>
                <div className="backtest-detail__headline-metric">
                    <Button size="small" icon={<StarFilled />} style={{ marginLeft: '10px', fontSize: 10, fontWeight: 700 }} onClick={() => handleArchive()}>{backtestDetail?.record_status === 1 ? '取消归档' : '归档'}</Button>
                    {
                        backtestDetail?.record_status === 0 && (
                            <Button size="small" icon={<CloseOutlined />} className='is-error' onClick={() => handleDelete()}>删除</Button>
                        )
                    }
                    <Button size="small" style={{ marginLeft: '10px', fontSize: 10, fontWeight: 700 }}>导出<b>PDF</b></Button>
                </div>
            </div>
            <div className="page-content">
                <div className="backtest-detail__summary-bar">
                    <span className="backtest-detail__chip">经理 <b className='backtest-detail__chip-name'>{backtestDetail?.manager_name ?? '--'}</b></span>
                    <span>区间 <b className='backtest-detail__chip-name'>{backtestDetail?.backtest_range_start} ~ {backtestDetail?.backtest_range_end}</b></span>
                    <span>K级别 <b className='backtest-detail__chip-name'>{backtestDetail?.config.freq ?? '--'}</b></span>
                    <span>初始资金 <b className='backtest-detail__chip-name'>￥{filterFormatWanYiNumber(backtestDetail?.initial_cash)}</b></span>
                    <span>手续费 <b className='backtest-detail__chip-name'>{backtestDetail?.config.commission != null ? `${backtestDetail.config.commission}%` : '--'}</b> </span>
                    <span>
                        当前状态 <strong className="is-positive">{backtestDetail?.status === 1 ? '已启用' : '已禁用'}</strong>
                    </span>
                </div>

                <div className="backtest-detail__metrics">
                    {metricCards.map((item) => (
                        <div key={item.label} className="backtest-detail__metric-card">
                            <div className={`backtest-detail__metric-value ${getMetricToneClass(item)}`}>
                                {getMetricDisplayValue(item.value)} {item.unit}
                            </div>
                            <div className="backtest-detail__metric-label">{item.label}</div>
                            <div className="backtest-detail__metric-sub">
                                <span>{item.subLabel}</span>
                                <span>{getMetricDisplayValue(item.subValue as MetricValueKey)} {item.subUnit}</span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="backtest-detail__tabs flex flex-between">
                    <Tabs defaultActiveKey="returns" size="small" items={tabs} onChange={(key) => changeTab(key as DetailTabKey)} />
                    {/* { 
                        activeTab === 'reason' && (
                            <div className="backtest-detail__tabs-days">
                                {spaceDaysList.map((item) => (
                                    <Tag variant='outlined' className={`color-3 ${spaceDays === item.days ? 'is-days-accent' : 'is-day-default'}`} key={item.days} onClick={() => setSpaceDays(item.days)}>
                                        {item.label}
                                    </Tag>
                                ))}
                            </div>
                        )
                    }     */}
                </div>

                {activeTab === 'returns' && (
                    <div className="backtest-detail__content">
                        <section className="backtest-detail__chart-card">
                            <div className='backtest-detail__chart-title'>
                                <div className="backtest-detail__chart-title-left">
                                    <div className="backtest-detail__chart-name">策略净值曲线</div>
                                    <div className="backtest-detail__chart-legend">
                                        <span className="is-strategy">策略</span>
                                        {showBenchmark && <span className="is-benchmark">{selectedBenchmark}</span>}
                                        {poolLegend && (
                                            <span
                                                className="is-pool"
                                                title="成分股前复权收盘价的等权日涨跌幅，复利后对齐到策略起始净值"
                                            >
                                                {poolLegend}
                                            </span>
                                        )}
                                    </div>
                                    {isNavSubWindow && (
                                        <span className="backtest-detail__chart-hint">区间起点归一为 1</span>
                                    )}
                                </div>
                                <div className="backtest-detail__chart-controls">
                                    <Select
                                        size="small"
                                        className="backtest-detail__year-select"
                                        value={selectedNavYear}
                                        options={yearOptions}
                                        disabled={!navDateBounds}
                                        onChange={changeNavYear}
                                    />
                                    <RangePicker
                                        size="small"
                                        className="backtest-detail__nav-range"
                                        allowClear={false}
                                        value={navRangeValue}
                                        disabled={!navDateBounds}
                                        placeholder={['开始日期', '结束日期']}
                                        disabledDate={(current: Dayjs) => {
                                            if (!current || !navDateBounds) return true
                                            const text = current.format('YYYY-MM-DD')
                                            return text < navDateBounds.start || text > navDateBounds.end
                                        }}
                                        onChange={(values) => {
                                            if (!values?.[0] || !values?.[1]) {
                                                setNavRange(null)
                                                return
                                            }
                                            setNavRange([
                                                values[0].format('YYYY-MM-DD'),
                                                values[1].format('YYYY-MM-DD'),
                                            ])
                                        }}
                                    />
                                    <Select
                                        size="small"
                                        className="backtest-detail__benchmark-select"
                                        value={selectedBenchmark}
                                        options={benchmarkOptions}
                                        onChange={setSelectedBenchmark}
                                        loading={benchmarkLoading}
                                    />
                                </div>
                            </div>
                            <div className="backtest-detail__chart">
                                <NavChart
                                    valueKey="nav_value"
                                    data={visibleChartNav}
                                    tooltipMode={isNavSubWindow ? 'growth' : 'value'}
                                    benchmarkSeries={showBenchmark ? {
                                        dataKey: 'benchmark_nav',
                                        label: selectedBenchmark,
                                    } : undefined}
                                    extraSeries={poolLegend ? [{
                                        dataKey: 'pool_nav',
                                        label: poolLegend,
                                        color: '#f59e0b',
                                        strokeDasharray: '2 3',
                                    }] : undefined}
                                />
                            </div>
                        </section>

                        <section className="backtest-detail__chart-card backtest-detail__chart-card--compact">
                            <div className="backtest-detail__chart-title">
                                <div className="backtest-detail__chart-title-left">
                                    <div className="backtest-detail__chart-name">仓位曲线</div>
                                    <div className="backtest-detail__chart-legend">
                                        <span className="is-position">仓位</span>
                                    </div>
                                </div>
                                <span className="backtest-detail__chart-hint">回测区间股票市值 / 总资产</span>
                            </div>
                            <div className="backtest-detail__chart">
                                <NavChart
                                    valueKey="position_pct"
                                    seriesName="仓位"
                                    color="#14b8a6"
                                    valueSuffix="%"
                                    tooltipDigits={2}
                                    data={positionSeries}
                                />
                            </div>
                        </section>

                        <section className="backtest-detail__chart-card backtest-detail__chart-card--compact">
                            <div className="backtest-detail__chart-title">回撤曲线</div>
                            <div className="backtest-detail__chart">
                                <NavChart valueKey="drawdown_pct" color="red" data={backtestNavDrawdownDetail || []} />
                            </div>
                        </section>
                    </div>
                )}

                {activeTab === 'stats' && (
                    <div className="backtest-detail__stats-grid">
                        {statsSections.map((section) => (
                            <section key={section.title} className="backtest-detail__stats-card">
                                <div className="backtest-detail__stats-title">{section.title}</div>
                                <div className="backtest-detail__stats-list">
                                    {section.items.map((item) => (
                                        <div key={item.label} className="backtest-detail__stats-row">
                                            <span className="backtest-detail__stats-label">{item.label}</span>
                                            <span className={`backtest-detail__stats-value ${getSectionClass(item)}`}>
                                                {
                                                    item.value === 'config.useMonitorSignals' ? backtestDetail?.config.useMonitorSignals ? '已启用' : '已禁用'
                                                        : <>{item.prefix}{getSectionDisplayValue(item)} {item.unit}</>
                                                }
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}

                {activeTab === 'trades' && (
                    <Table<BacktestTradesDetail>
                        rowKey={(record) => `${record.sequence_no}-${record.datetime}-${record.code}-${record.side}`}
                        columns={columns}
                        dataSource={backtestTradesDetail ?? []}
                    />
                )}

                {activeTab === 'holdings' && (
                    <section className="backtest-detail__holdings">
                        <div className="backtest-detail__holdings-toolbar">
                            <span>查询日期</span>
                            <DatePicker
                                size="small"
                                allowClear={false}
                                value={holdingsDate ? dayjs(holdingsDate) : null}
                                disabled={!backtestDetail?.backtest_range_start || !backtestDetail?.backtest_range_end}
                                disabledDate={(current: Dayjs) => {
                                    if (!current) return true
                                    const start = (backtestDetail?.backtest_range_start || '').slice(0, 10)
                                    const end = (backtestDetail?.backtest_range_end || '').slice(0, 10)
                                    const text = current.format('YYYY-MM-DD')
                                    return !start || !end || text < start || text > end
                                }}
                                onChange={(value) => {
                                    if (!value) return
                                    const text = value.format('YYYY-MM-DD')
                                    setHoldingsDate(text)
                                    void loadHoldings(text)
                                }}
                            />
                            <span>
                                回测区间 {backtestDetail?.backtest_range_start || '--'} ~ {backtestDetail?.backtest_range_end || '--'}
                            </span>
                        </div>
                        {holdingsSnapshot && (
                            <div className="backtest-detail__holdings-summary">
                                <span>持仓 <b>{holdingsSnapshot.position_count}</b> 只</span>
                                <span>持仓市值 <b>￥{filterFormatWanYiNumber(holdingsSnapshot.market_value)}</b></span>
                                <span>现金 <b>￥{filterFormatWanYiNumber(holdingsSnapshot.cash)}</b></span>
                                <span>总资产 <b>￥{filterFormatWanYiNumber(holdingsSnapshot.total_assets)}</b></span>
                                {holdingsSnapshot.initial_cash > 0 && (
                                    <span>
                                        相对初始资金 <b>{filterFormatThousandNumber((holdingsSnapshot.total_assets / holdingsSnapshot.initial_cash - 1) * 100)}%</b>
                                    </span>
                                )}
                                {(holdingsSnapshot.cash_interest ?? 0) > 0 && (
                                    <span>
                                        现金利息 <b>￥{filterFormatWanYiNumber(holdingsSnapshot.cash_interest)}</b>
                                        {holdingsSnapshot.cash_rate_annual
                                            ? `（年化 ${(holdingsSnapshot.cash_rate_annual * 100).toFixed(2)}%）`
                                            : ''}
                                    </span>
                                )}
                                {holdingsSnapshot.dividend_cash ? (
                                    <span>累计分红计入现金 <b>￥{filterFormatWanYiNumber(holdingsSnapshot.dividend_cash)}</b></span>
                                ) : null}
                            </div>
                        )}
                        {holdingsLoading ? (
                            <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin /></div>
                        ) : (
                            <Table
                                rowKey="code"
                                columns={holdingsColumns}
                                dataSource={holdingsSnapshot?.items ?? []}
                                pagination={false}
                                locale={{ emptyText: holdingsDate ? '该日没有持仓' : '选择回测区间内的日期' }}
                            />
                        )}
                    </section>
                )}

                {activeTab === 'reason' && (
                    <section className="backtest-detail__reason-card">
                        <div className='backtest-detail__reason-desc'>
                            各标的贡献度（一股一行）· 已实现 + 期末浮盈 + 分红
                            {reasonSummary.open_position_count > 0 && (
                                <span style={{ marginLeft: 12 }}>
                                    期末持仓 {reasonSummary.open_position_count} 只 · 浮盈
                                    <b style={{
                                        marginLeft: 4,
                                        color: reasonSummary.open_unrealized_pnl >= 0 ? 'var(--red)' : 'var(--green)',
                                    }}>
                                        {reasonSummary.open_unrealized_pnl >= 0 ? '+' : ''}
                                        {filterFormatWanYiNumber(reasonSummary.open_unrealized_pnl)}
                                    </b>
                                </span>
                            )}
                        </div>
                        {loading ? <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin /></div> : (
                            <div className="backtest-detail__reason">
                                {backtestReasonDetail.map((item) => {
                                    const maxAbs = Math.max(
                                        10,
                                        ...backtestReasonDetail.map((x) => Math.abs(Number(x.contribution_pct) || 0)),
                                    )
                                    const openShares = Number(item.open_shares || 0)
                                    return (
                                    <Fragment key={item.code}>
                                        <div className="backtest-detail__score-row">
                                            <div className="backtest-detail__score-code">{item.code}</div>
                                            <div className="backtest-detail__score-dim">{item.stock_name}</div>
                                            <div className="backtest-detail__score-bar-wrap">
                                                <Progress
                                                    percent={Math.abs(item.contribution_pct) / maxAbs * 100}
                                                    showInfo={false}
                                                    strokeColor={item.contribution_pct > 0 ? 'var(--red)' : 'var(--green)'}
                                                    size={['100%', 12]}
                                                />
                                            </div>
                                            <div className="backtest-detail__score-percent" style={{ color: item.contribution_pct > 0 ? 'var(--red)' : 'var(--green)' }}>
                                                {item.contribution_pct > 0 ? '+' + item.contribution_pct : item.contribution_pct}%
                                            </div>
                                            <div className="backtest-detail__score-days" title="股数加权平均持有自然日">
                                                加权{item.holding_days}天
                                            </div>
                                        </div>
                                        <div className="backtest-detail__score-sub">
                                            {item.holding_period && (
                                                <>
                                                    持仓 {item.holding_period}
                                                    {Number(item.open_shares || 0) > 0 && Number(item.open_holding_days || 0) > 0 && (
                                                        <> · 现仓已持 {item.open_holding_days} 天</>
                                                    )}
                                                    <span style={{ margin: '0 8px' }}>·</span>
                                                </>
                                            )}
                                            已实现 {filterFormatWanYiNumber(item.realized_pnl)}
                                            <span style={{ margin: '0 8px' }}>·</span>
                                            期末浮盈 {filterFormatWanYiNumber(item.unrealized_pnl)}
                                            {(item.dividend_pnl ?? 0) !== 0 && (
                                                <>
                                                    <span style={{ margin: '0 8px' }}>·</span>
                                                    分红 {filterFormatWanYiNumber(item.dividend_pnl || 0)}
                                                </>
                                            )}
                                            {openShares > 0 && (
                                                <>
                                                    <span style={{ margin: '0 8px' }}>·</span>
                                                    仍持仓 {filterFormatThousandNumber(openShares, 0)} 股
                                                    {item.end_price != null && (
                                                        <> @ {filterFormatThousandNumber(Number(item.end_price))}</>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </Fragment>
                                    )
                                })}
                            </div>
                        )}
                    </section>
                )}

                {activeTab === 'config' && (
                    <div className="backtest-detail__content">
                        <section className="backtest-detail__config-card">
                            <div className='backtest-detail__config-title'>
                                <UserOutlined /> 基金经理
                            </div>
                            <div className='flex'>
                                <div className="backtest-detail__config-hero-icon">{backtestDetail?.manager.initials ?? '--'}</div>
                                <div className="backtest-detail__config-hero-main">
                                    <div className="backtest-detail__config-name">{backtestDetail?.manager.nickname ?? '--'}</div>
                                    <div className="backtest-detail__config-subtitle">
                                        {backtestDetail?.manager.style || '--'}
                                    </div>
                                </div>
                            </div>
                            <div className="backtest-detail__config-summary">
                                {backtestDetail?.manager.investment_brief || '暂无投资理念'}
                            </div>
                        </section>

                        <section className="backtest-detail__config-card">
                            <div className='backtest-detail__config-title'>
                                <FileOutlined /> 策略版本
                            </div>
                            <div className="backtest-detail__config-version flex flex-between">
                                <div>
                                    <span className="backtest-detail__config-version-name">{backtestDetail?.strategy_version_no ?? '--'}</span>
                                    <span className="backtest-detail__config-version-type">{backtestDetail?.strategy_version_name ?? '--'}</span>
                                </div>
                            </div>
                            <div className="backtest-detail__config-desc">
                                <div className="backtest-detail__config-desc-label text-10">核心策略描述</div>
                                <div className="backtest-detail__config-desc-content">
                                    {backtestDetail?.strategy_snapshot}
                                </div>
                            </div>
                            {backtestDetail?.focus_sectors && backtestDetail?.focus_sectors?.length > 0 &&
                                <>
                                    <div className="backtest-detail__config-desc-label text-10">关注板块</div>
                                    <div className="backtest-detail__config-focus_sectors-content flex gap-8">
                                        {backtestDetail?.focus_sectors?.map((sector) => (
                                            <Tag
                                                key={sector}
                                                className="border-tag text-11"
                                            >
                                                {sector}
                                            </Tag>
                                        ))}
                                    </div>
                                </>
                            }
                            {backtestDetail?.constraints && backtestDetail?.constraints?.length > 0 &&
                                <>
                                    <div className="backtest-detail__config-desc-label text-10">风险约束</div>
                                    <div className="backtest-detail__config-constraint-content gird gap-8">
                                        {backtestDetail?.constraints?.map((constraint, index) => (
                                            <div key={constraint} style={{ marginRight: 12, marginBottom: 12 }}>
                                                <span className="backtest-detail__config-desc-content-dot" > {index + 1} </span>
                                                <span className="backtest-detail__config-desc-content-text" > {constraint} </span>
                                            </div>
                                        ))}
                                    </div>

                                </>
                            }
                        </section>

                        <section className="backtest-detail__config-card">
                            <div className='backtest-detail__config-title'>
                                <CalendarOutlined /> 回测配置
                            </div>
                            <div className="backtest-detail__config-grid">
                                {runtimeConfigItems.map((item, index) => (
                                    <div key={item.label} className="backtest-detail__config-item">
                                        <div className="backtest-detail__config-item-label">{item.label}</div>
                                        <div className={`backtest-detail__config-item-value ${index === runtimeConfigItems.length - 1 && backtestDetail?.config.useMonitorSignals ? 'green' : ''}`}>{item.value}</div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                )}

                {
                    activeTab === 'ai_signals' && (
                        <section className="backtest-detail__ai-signals-card">
                            <div className='backtest-detail__ai-signals-tabs'>
                                {aiSignalsTabs.map((item) => (
                                    <Tag key={item.key} className={`backtest-detail__ai-signals-tag text-12 is-round ${aiSignalsKeys === item.key ? 'active' : ''}`} onClick={() => changeAiSignals(item.key)}>{item.label}</Tag>
                                ))}
                            </div>
                            {signalsLoading ? <div style={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin /></div> : 
                                (
                                    aiSignalsKeys === 'fundamental' ? (
                                        fundamentalSignals.length > 0 ? fundamentalSignals.map((item) => (
                                            <AISignalsItem key={item.id} backtestId={backtestDetail?.id ?? 0} signal={item} trades={backtestTradesDetail ?? []} />
                                        )) : <Empty style={{ marginTop: 64 }} description="暂无数据" />
                                    ):
                                    aiSignalsKeys === 'kline' ? (
                                        klineSignals.length > 0 ? klineSignals.map((item) => (
                                            <AISignalsItem key={item.id} backtestId={backtestDetail?.id ?? 0} signal={item} trades={backtestTradesDetail ?? []} />
                                        )) : <Empty style={{ marginTop: 64 }} description="暂无数据" />
                                    ):
                                    aiSignalsKeys === 'report' && (
                                        reportSignals.length > 0 ? reportSignals.map((item) => (
                                            <AISignalsItem key={item.id} backtestId={backtestDetail?.id ?? 0} signal={item} trades={backtestTradesDetail ?? []} />
                                        )) : <Empty style={{ marginTop: 64 }} description="暂无数据" />
                                    )
                                )
                            }
                            {signalsMeta[aiSignalsKeys].total > SIGNALS_PAGE_SIZE ? (
                                <div className="backtest-detail__ai-signals-pagination">
                                    <Pagination
                                        size="small"
                                        current={signalsMeta[aiSignalsKeys].page}
                                        pageSize={SIGNALS_PAGE_SIZE}
                                        total={signalsMeta[aiSignalsKeys].total}
                                        showSizeChanger={false}
                                        showTotal={(total) => `共 ${total} 条`}
                                        onChange={changeSignalsPage}
                                    />
                                </div>
                            ) : null}
                        </section>
                    )
                }

                <Modal
                    title={signalModalDetail ? `决策信号 #${signalModalDetail.id}` : '决策信号'}
                    open={signalModalOpen}
                    onCancel={() => setSignalModalOpen(false)}
                    footer={null}
                    width={720}
                    destroyOnClose
                >
                    {signalModalLoading ? (
                        <div style={{ padding: '48px 0', textAlign: 'center' }}>
                            <Spin />
                        </div>
                    ) : signalModalDetail ? (
                        <div className="backtest-detail__signal-modal">
                            <div className="backtest-detail__signal-modal-meta">
                                <Tag>{signalModalDetail.signal_type}</Tag>
                                <span>{signalModalDetail.signal_date}</span>
                                {signalModalDetail.stock_code ? <span>{signalModalDetail.stock_code}</span> : null}
                                {signalModalDetail.rule_name ? <span>{signalModalDetail.rule_name}</span> : null}
                            </div>
                            <AISignalDetailBody
                                detail={signalModalDetail}
                                trades={(backtestTradesDetail ?? []).filter(trade => trade.signal_id === signalModalDetail.id)}
                            />
                        </div>
                    ) : (
                        <Empty description="信号详情加载失败" />
                    )}
                </Modal>
            </div>
        </div>
    )
}
